import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IpponButton,
  IpponCard,
  IpponField,
  IpponHSpace,
  IpponHSpaceSlot,
  IpponInputText,
  IpponLabel,
  IpponSeparator,
  IpponSingleCombobox,
  IpponVSpace,
} from '@ippon-ui/react';
import { Optional } from '@/generic/functional';
import { ExampleCompliance, Severity } from '@/rule';
import {
  DraftExample,
  type DraftRule,
  type DraftRuleEdit,
} from '@/rules-management/domain/DraftRule.ts';

type ExampleFormState = {
  description: string;
  snippet: string;
  compliance: ExampleCompliance | undefined;
};

type FormState = {
  title: string;
  description: string;
  severity: Severity | undefined;
  examples: ExampleFormState[];
};

const toExampleFormState = (example: DraftExample): ExampleFormState => ({
  description: example.description.orElse(''),
  snippet: example.snippet.orElse(''),
  compliance: example.compliance.orUndefined(),
});

const toFormState = (draft: DraftRule): FormState => ({
  title: draft.title.map((title) => title.value).orElse(''),
  description: draft.description.map((description) => description.value).orElse(''),
  severity: draft.severity.orUndefined(),
  examples: draft.examples.map(toExampleFormState),
});

const toEdit = (form: FormState): DraftRuleEdit => ({
  title: Optional.ofFalsifiable(form.title),
  description: Optional.ofFalsifiable(form.description),
  severity: Optional.ofUndefinable(form.severity),
  examples: form.examples.map((example) =>
    DraftExample.of({
      description: Optional.ofFalsifiable(example.description),
      snippet: Optional.ofFalsifiable(example.snippet),
      compliance: Optional.ofUndefinable(example.compliance),
    }),
  ),
});

const EMPTY_EXAMPLE: ExampleFormState = {
  description: '',
  snippet: '',
  compliance: undefined,
};

const SEVERITY_OPTIONS = [
  { key: 'CRITICAL', value: Severity.CRITICAL },
  { key: 'HIGH', value: Severity.HIGH },
  { key: 'MEDIUM', value: Severity.MEDIUM },
  { key: 'LOW', value: Severity.LOW },
] as const;

const COMPLIANCE_OPTIONS = [
  { key: 'COMPLIANT', value: ExampleCompliance.COMPLIANT },
  { key: 'NON_COMPLIANT', value: ExampleCompliance.NON_COMPLIANT },
] as const;

const SeverityField = ({
  id,
  dataSelector,
  severity,
  onChange,
}: {
  id: string;
  dataSelector: string;
  severity: Severity | undefined;
  onChange: (severity: Severity) => void;
}) => {
  const { t } = useTranslation('rule');
  const [query, setQuery] = useState('');

  const options = SEVERITY_OPTIONS.map((option) => ({
    key: option.key,
    label: t(`rule.severity.${option.key.toLowerCase()}`),
  }));
  const selection = options.find(
    (option) =>
      SEVERITY_OPTIONS.find((severityOption) => severityOption.key === option.key)?.value ===
      severity,
  );

  return (
    <IpponSingleCombobox
      id={id}
      dataSelector={dataSelector}
      options={options}
      query={query}
      onQueryChange={setQuery}
      selection={selection}
      onSelect={(option) => {
        setQuery('');
        const picked = SEVERITY_OPTIONS.find((severityOption) => severityOption.key === option.key);
        if (picked) {
          onChange(picked.value);
        }
      }}
    />
  );
};

const ComplianceField = ({
  id,
  dataSelector,
  compliance,
  onChange,
}: {
  id: string;
  dataSelector: string;
  compliance: ExampleCompliance | undefined;
  onChange: (compliance: ExampleCompliance) => void;
}) => {
  const { t } = useTranslation('rules-management');
  const [query, setQuery] = useState('');

  const options = COMPLIANCE_OPTIONS.map((option) => ({
    key: option.key,
    label: t(
      `rules-management.rules.import.result.rule.edit.example.${
        option.key === 'COMPLIANT' ? 'compliant' : 'non-compliant'
      }`,
    ),
  }));
  const selection = options.find(
    (option) =>
      COMPLIANCE_OPTIONS.find((complianceOption) => complianceOption.key === option.key)?.value ===
      compliance,
  );

  return (
    <IpponSingleCombobox
      id={id}
      dataSelector={dataSelector}
      options={options}
      query={query}
      onQueryChange={setQuery}
      selection={selection}
      onSelect={(option) => {
        setQuery('');
        const picked = COMPLIANCE_OPTIONS.find(
          (complianceOption) => complianceOption.key === option.key,
        );
        if (picked) {
          onChange(picked.value);
        }
      }}
    />
  );
};

const ExampleFormComponent = ({
  id,
  example,
  onChange,
  onRemove,
}: {
  id: string;
  example: ExampleFormState;
  onChange: (example: ExampleFormState) => void;
  onRemove: () => void;
}) => {
  const { t } = useTranslation('rules-management');

  return (
    <IpponCard border={true} dataSelector="draft-rule.edit.example">
      <IpponVSpace gap={8}>
        <IpponHSpace gap={16}>
          <IpponHSpaceSlot expand={true}>
            <IpponField>
              <IpponLabel htmlFor={`${id}-description`}>
                {t('rules-management.rules.import.result.rule.edit.example.description')}
              </IpponLabel>
              <IpponInputText
                id={`${id}-description`}
                value={example.description}
                onChange={(event) => onChange({ ...example, description: event.target.value })}
                dataSelector="draft-rule.edit.example.description"
              />
            </IpponField>
          </IpponHSpaceSlot>
          <IpponHSpaceSlot expand={true}>
            <IpponField>
              <IpponLabel htmlFor={`${id}-compliance`}>
                {t('rules-management.rules.import.result.rule.edit.example.compliance')}
              </IpponLabel>
              <ComplianceField
                id={`${id}-compliance`}
                dataSelector="draft-rule.edit.example.compliance"
                compliance={example.compliance}
                onChange={(compliance) => onChange({ ...example, compliance })}
              />
            </IpponField>
          </IpponHSpaceSlot>
        </IpponHSpace>
        <IpponField>
          <IpponLabel htmlFor={`${id}-snippet`}>
            {t('rules-management.rules.import.result.rule.edit.example.snippet')}
          </IpponLabel>
          <IpponInputText
            id={`${id}-snippet`}
            value={example.snippet}
            onChange={(event) => onChange({ ...example, snippet: event.target.value })}
            dataSelector="draft-rule.edit.example.snippet"
          />
        </IpponField>
        <IpponButton
          color="error"
          variant="text"
          onClick={onRemove}
          iconLeft={{ name: 'trash', variant: 'outline' }}
          dataSelector="draft-rule.edit.example.remove"
        >
          {t('rules-management.rules.import.result.rule.edit.example.remove')}
        </IpponButton>
      </IpponVSpace>
    </IpponCard>
  );
};

export const DraftRuleEditComponent = ({
  draft,
  onSave,
  onCancel,
}: {
  draft: DraftRule;
  onSave: (edit: DraftRuleEdit) => Promise<boolean>;
  onCancel: () => void;
}) => {
  const { t } = useTranslation('rules-management');
  const [form, setForm] = useState<FormState>(() => toFormState(draft));

  const handleAddExample = () =>
    setForm((current) => ({ ...current, examples: [...current.examples, { ...EMPTY_EXAMPLE }] }));

  const handleRemoveExample = (index: number) =>
    setForm((current) => ({
      ...current,
      examples: current.examples.filter((_, exampleIndex) => exampleIndex !== index),
    }));

  const handleExampleChange = (index: number, example: ExampleFormState) =>
    setForm((current) => ({
      ...current,
      examples: current.examples.map((item, exampleIndex) =>
        exampleIndex === index ? example : item,
      ),
    }));

  const handleSave = (): Promise<void> => onSave(toEdit(form)).then(() => undefined);

  const id = `draft-rule-${draft.id.value}-edit`;

  return (
    <IpponVSpace gap={16} dataSelector="draft-rule.edit">
      <IpponHSpace gap={16}>
        <IpponHSpaceSlot expand={true}>
          <IpponField>
            <IpponLabel htmlFor={`${id}-title`}>
              {t('rules-management.rules.import.result.rule.edit.title')}
            </IpponLabel>
            <IpponInputText
              id={`${id}-title`}
              value={form.title}
              onChange={(event) =>
                setForm((current) => ({ ...current, title: event.target.value }))
              }
              dataSelector="draft-rule.edit.title"
            />
          </IpponField>
        </IpponHSpaceSlot>
        <IpponHSpaceSlot expand={true}>
          <IpponField>
            <IpponLabel htmlFor={`${id}-severity`}>
              {t('rules-management.rules.import.result.rule.edit.severity')}
            </IpponLabel>
            <SeverityField
              id={`${id}-severity`}
              dataSelector="draft-rule.edit.severity"
              severity={form.severity}
              onChange={(severity) => setForm((current) => ({ ...current, severity }))}
            />
          </IpponField>
        </IpponHSpaceSlot>
      </IpponHSpace>
      <IpponField>
        <IpponLabel htmlFor={`${id}-description`}>
          {t('rules-management.rules.import.result.rule.edit.description')}
        </IpponLabel>
        <IpponInputText
          id={`${id}-description`}
          value={form.description}
          onChange={(event) =>
            setForm((current) => ({ ...current, description: event.target.value }))
          }
          dataSelector="draft-rule.edit.description"
        />
      </IpponField>
      <IpponSeparator />
      <IpponVSpace gap={8}>
        {form.examples.map((example, index) => (
          <ExampleFormComponent
            key={`${id}-example-${index}`}
            id={`${id}-example-${index}`}
            example={example}
            onChange={(example) => handleExampleChange(index, example)}
            onRemove={() => handleRemoveExample(index)}
          />
        ))}
        <IpponButton
          color="neutral"
          variant="outline"
          onClick={handleAddExample}
          iconLeft={{ name: 'add' }}
          dataSelector="draft-rule.edit.example.add"
        >
          {t('rules-management.rules.import.result.rule.edit.example.add')}
        </IpponButton>
      </IpponVSpace>
      <IpponHSpace gap={8}>
        <IpponButton
          color="neutral"
          variant="outline"
          onClick={onCancel}
          dataSelector="draft-rule.edit.cancel"
        >
          {t('rules-management.rules.import.result.rule.edit.cancel')}
        </IpponButton>
        <IpponButton onClick={handleSave} dataSelector="draft-rule.edit.save">
          {t('rules-management.rules.import.result.rule.edit.save')}
        </IpponButton>
      </IpponHSpace>
    </IpponVSpace>
  );
};
